import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Shield, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const submittingRef = useRef(false);
  
  // Check if already logged in as admin
  useEffect(() => {
    const token = localStorage.getItem('bpm_admin_token');
    if (token) {
      fetch(`${API}/api/admin/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => {
          if (res.ok) navigate('/admin');
        })
        .catch(() => {});
    }
  }, [navigate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;  // anti-double-submit (Enter + click race)
    submittingRef.current = true;
    setError('');
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      // On error responses, do NOT read the body, some dev/runtime layers
      // (CRA dev overlay, browser interceptors) consume non-ok response bodies
      // before our handler can. Use the status code to derive the error message.
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Email ou mot de passe incorrect');
        }
        if (response.status === 429) {
          throw new Error('Trop de tentatives. Réessayez dans 15 minutes.');
        }
        throw new Error('Erreur de connexion. Veuillez réessayer.');
      }
      
      // Success: now it is safe to consume the body
      let data = {};
      try { data = await response.json(); } catch (_e) { data = {}; }
      
      if (!data.access_token) {
        throw new Error('Réponse serveur invalide.');
      }
      
      // Store admin tokens
      localStorage.setItem('bpm_admin_token', data.access_token);
      localStorage.setItem('bpm_admin_refresh', data.refresh_token);
      localStorage.setItem('bpm_admin', JSON.stringify(data.admin));
      
      navigate('/admin');
      
    } catch (err) {
      setError(err.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };
  
  return (
    <div className="min-h-screen bg-[#111827] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-orange-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-orange-500" />
          </div>
          <h1 className="text-2xl font-bold text-white">Administration BPM</h1>
          <p className="text-gray-500 text-sm mt-1">Accès réservé aux administrateurs</p>
        </div>
        
        {/* Login Card */}
        <div className="bg-[#1F2937] rounded-2xl p-8 border border-gray-700">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-gray-400 text-sm mb-2">Email administrateur</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-[#111827] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                placeholder="admin@bpm.com"
              />
            </div>
            
            <div>
              <label className="block text-gray-400 text-sm mb-2">Mot de passe</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-[#111827] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  Se connecter
                </>
              )}
            </button>
          </form>
        </div>
        
        {/* Footer */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-gray-500 hover:text-gray-300 text-sm">
            ← Retour au site
          </Link>
        </div>
      </div>
    </div>
  );
}
